import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Ticket, Mail, Shield, Plane, Plus, Clock, Zap, Database } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [eventUrl, setEventUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);

  // Fetch last sync time and cache status
  useEffect(() => {
    const fetchSyncInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/sync-info`);
        const data = await res.json();
        if (data.last_sync) {
          setLastSync(new Date(data.last_sync));
        }
      } catch (e) {
        console.debug("Sync info unavailable");
      }
    };
    
    const fetchCacheStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/cache/stats`);
        const data = await res.json();
        if (data.cache) {
          setCacheStatus(data.cache);
        }
      } catch (e) {
        console.debug("Cache status unavailable");
      }
    };
    
    fetchSyncInfo();
    fetchCacheStatus();
    
    // Refresh cache status every 30 seconds
    const interval = setInterval(fetchCacheStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmitEvent = async () => {
    if (!eventUrl.trim()) {
      toast.error("Please enter an event URL");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/submit-event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: eventUrl })
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success("Event submitted for review!");
        setEventUrl("");
        setShowSubmitModal(false);
      } else {
        toast.error(data.detail || "Invalid event URL");
      }
    } catch (e) {
      toast.error("Failed to submit event");
    } finally {
      setSubmitting(false);
    }
  };

  const formatSyncTime = (date) => {
    if (!date) return null;
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60); // minutes
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <footer className="py-12 border-t border-border bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <Ticket className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-heading text-xl font-bold text-foreground">XTRIP</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Event-first travel for electronic music fans.
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="w-4 h-4 text-primary" />
              <a 
                href="mailto:hello@xtriptour.com" 
                className="hover:text-primary transition-colors"
              >
                hello@xtriptour.com
              </a>
            </div>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-heading font-bold text-foreground mb-4 uppercase tracking-wider text-sm">
              Legal
            </h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/terms" 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy" 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/refund" 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Refund Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="font-heading font-bold text-foreground mb-4 uppercase tracking-wider text-sm">
              Destinations
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>Berlin, Germany</li>
              <li>Amsterdam, Netherlands</li>
              <li>London, UK</li>
              <li>Ibiza, Spain</li>
              <li>Barcelona, Spain</li>
            </ul>
          </div>

          {/* Submit Event */}
          <div>
            <h4 className="font-heading font-bold text-foreground mb-4 uppercase tracking-wider text-sm">
              Add Event
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              Know an RA or Xceed event we're missing?
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSubmitModal(true)}
              className="w-full justify-center gap-2"
              data-testid="submit-event-btn"
            >
              <Plus className="w-4 h-4" />
              Submit Event
            </Button>
            
            {/* Last Sync Indicator */}
            {lastSync && (
              <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>Synced {formatSyncTime(lastSync)}</span>
              </div>
            )}
            
            {/* Cache Status Indicator */}
            {cacheStatus && (
              <div 
                className="mt-2 flex items-center gap-1.5 text-xs"
                data-testid="cache-status-indicator"
              >
                {cacheStatus.total_entries > 0 ? (
                  <>
                    <div className="relative">
                      <Database className="w-3 h-3 text-cyan-400" />
                      <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
                    </div>
                    <span className="text-cyan-400 font-mono">
                      {cacheStatus.fresh_count > 0 ? (
                        <>⚡ {cacheStatus.fresh_count} cached</>
                      ) : (
                        <>📦 {cacheStatus.stale_count} stale</>
                      )}
                    </span>
                  </>
                ) : (
                  <>
                    <Zap className="w-3 h-3 text-green-400" />
                    <span className="text-green-400 font-mono">Live data</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Submit Event Modal */}
        {showSubmitModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg p-6 max-w-md w-full border border-border">
              <h3 className="font-heading font-bold text-lg mb-2">Submit an Event</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Paste a Resident Advisor or Xceed event URL below.
              </p>
              <input
                type="url"
                value={eventUrl}
                onChange={(e) => setEventUrl(e.target.value)}
                placeholder="https://ra.co/events/123456"
                className="w-full px-3 py-2 rounded-md bg-background border border-border text-foreground mb-4"
                data-testid="event-url-input"
              />
              <div className="flex gap-2 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSubmitModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSubmitEvent}
                  disabled={submitting}
                  data-testid="submit-event-confirm"
                >
                  {submitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Trust & Partner Logos */}
        <div className="py-6 border-t border-b border-border mb-8">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {/* XTrip Curated */}
            <div className="flex items-center gap-2 text-muted-foreground/60 hover:text-muted-foreground transition-colors">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Ticket className="w-4 h-4 text-primary" />
              </div>
              <div className="text-xs">
                <div className="font-mono uppercase tracking-wider">Curated by</div>
                <div className="font-medium text-foreground/80">XTrip</div>
              </div>
            </div>
            
            {/* Duffel */}
            <div className="flex items-center gap-2 text-muted-foreground/60 hover:text-muted-foreground transition-colors">
              <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
                <Plane className="w-4 h-4 text-secondary" />
              </div>
              <div className="text-xs">
                <div className="font-mono uppercase tracking-wider">Powered by</div>
                <div className="font-medium text-foreground/80">Duffel</div>
              </div>
            </div>
            
            {/* Stripe */}
            <div className="flex items-center gap-2 text-muted-foreground/60 hover:text-muted-foreground transition-colors">
              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                <Shield className="w-4 h-4 text-accent" />
              </div>
              <div className="text-xs">
                <div className="font-mono uppercase tracking-wider">Secured by</div>
                <div className="font-medium text-foreground/80">Stripe</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © 2026 XTrip. All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground font-mono">
            XTrip Travel Engine • EU & UK
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
